import { useEffect, useState } from 'react'

import type { CatalogImportRowFieldOverride, ImportPreviewRowDto } from '../api/catalogImportTypes'
import type { ProductTypeDto } from '../api/productTypes'
import { ProductTypeCascadePicker } from './ProductTypeCascadePicker'
import { SuggestTextField } from './SuggestTextField'

type ImportRowEditPanelProps = {
  row: ImportPreviewRowDto
  selectedProductTypeId: number | null
  /**
   * Оператор явно выбрал тип, а не оставил строку как есть. Без этого нетронутая строка,
   * чей сохранённый тип не совпал с предложением матчера, читалась бы как отказ от
   * предложения.
   */
  typePickedByOperator?: boolean
  fieldOverride: CatalogImportRowFieldOverride | null
  productTypes: ProductTypeDto[]
  brandOptions?: string[]
  countryOptions?: string[]
  onTypeChange: (productTypeId: number | null) => void
  onFieldOverrideChange: (override: CatalogImportRowFieldOverride | null) => void
}

const buildFieldOverride = (
  row: ImportPreviewRowDto,
  name: string,
  brand: string,
  country: string,
  price: string,
  unit: string,
  minShipmentQty: string,
  packageSize: string,
  comment: string,
): CatalogImportRowFieldOverride | null => {
  const originalName = row.name?.trim() ?? ''
  const originalBrand = row.brand?.trim() ?? ''
  const originalCountry = row.country?.trim() ?? ''
  const originalPrice = row.price?.trim() ?? ''
  const originalUnit = row.unit?.trim() ?? ''
  const originalMinShipment = row.minShipmentQty?.trim() ?? ''
  const originalPackageSize = row.packageSize?.trim() ?? ''
  const nameChanged = name.trim() !== originalName
  const brandChanged = brand.trim() !== originalBrand
  const countryChanged = country.trim() !== originalCountry
  const priceChanged = price.trim() !== originalPrice
  const unitChanged = unit.trim() !== originalUnit
  const minShipmentChanged = minShipmentQty.trim() !== originalMinShipment
  const packageSizeChanged = packageSize.trim() !== originalPackageSize
  const commentChanged = comment.trim().length > 0

  if (
    !nameChanged &&
    !brandChanged &&
    !countryChanged &&
    !priceChanged &&
    !unitChanged &&
    !minShipmentChanged &&
    !packageSizeChanged &&
    !commentChanged
  ) {
    return null
  }

  return {
    name: nameChanged ? name.trim() : undefined,
    brand: brandChanged ? brand.trim() : undefined,
    country: countryChanged ? country.trim() : undefined,
    price: priceChanged ? price.trim() : undefined,
    unit: unitChanged ? unit.trim() : undefined,
    minShipmentQty: minShipmentChanged ? minShipmentQty.trim() : undefined,
    packageSize: packageSizeChanged ? packageSize.trim() : undefined,
    comment: commentChanged ? comment.trim() : undefined,
  }
}

type CorrectionReasonHint = {
  value:
    | 'wrong_brand'
    | 'wrong_species_type'
    | 'wrong_packaging'
    | 'wrong_price'
    | 'not_in_dictionary'
    | 'suggestion_rejected'
    | 'other'
  label: string
}

const REASON_LABELS: Record<CorrectionReasonHint['value'], string> = {
  wrong_brand: 'Бренд',
  wrong_species_type: 'Вид / сорт',
  wrong_packaging: 'Фасовка',
  wrong_price: 'Цена',
  not_in_dictionary: 'Нет в справочнике',
  suggestion_rejected: 'Оставлено своё',
  other: 'Другое',
}

const normalizeForDiff = (value: string | null | undefined): string =>
  (value ?? '').trim().toLocaleLowerCase('ru-RU')

/**
 * Mirror of the backend's CatalogMatchCorrectionReasonResolver — shows the operator which
 * reason will be recorded, without asking them anything.
 *
 * The two drifted apart once: this side counted a type change from the matcher's top
 * candidate while the backend counted it from the item's stored type, and `not_in_dictionary`
 * fired on different conditions. The operator saw one reason, the training record got
 * another. Expectations now live in one place for both sides —
 * `@provisium/web-api/correction-reason-cases.json`, checked by
 * ImportRowEditPanel.reason.test.tsx here and CatalogMatchCorrectionReasonContractTest in
 * core. Change the rule and change that table, or both suites fail.
 */
export const inferCorrectionReason = (input: {
  previousProductTypeId: number | null
  chosenProductTypeId: number | null
  matcherSuggestedTypeId: number | null
  operatorPickedType: boolean
  previousBrand?: string | null
  chosenBrand?: string | null
  previousPackageSize?: string | null
  chosenPackageSize?: string | null
  previousPrice?: string | null
  chosenPrice?: string | null
}): CorrectionReasonHint | null => {
  const hint = (value: CorrectionReasonHint['value']): CorrectionReasonHint => ({
    value,
    label: REASON_LABELS[value],
  })

  // Ни применённого типа, ни кандидата — сравнивать не с чем.
  if (input.previousProductTypeId == null && input.matcherSuggestedTypeId == null) {
    return hint('not_in_dictionary')
  }

  const typeChanged = input.previousProductTypeId !== input.chosenProductTypeId
  const suggestionRejected =
    !typeChanged &&
    input.operatorPickedType &&
    input.matcherSuggestedTypeId != null &&
    input.chosenProductTypeId !== input.matcherSuggestedTypeId

  const changed = (previous: string | null | undefined, chosen: string | null | undefined): boolean =>
    chosen != null && normalizeForDiff(previous) !== normalizeForDiff(chosen)

  const brandChanged = changed(input.previousBrand, input.chosenBrand)
  const packageChanged = changed(input.previousPackageSize, input.chosenPackageSize)
  const priceChanged = changed(input.previousPrice, input.chosenPrice)

  // Отказ от предложения — такая же грань типа, как и смена: в счётчик идёт одна.
  const typeFacetTouched = typeChanged || suggestionRejected
  const changedCount = [typeFacetTouched, brandChanged, packageChanged, priceChanged].filter(
    Boolean,
  ).length

  if (changedCount === 0) {
    return null
  }
  if (changedCount > 1) {
    return hint('other')
  }
  if (typeChanged) {
    return hint('wrong_species_type')
  }
  if (suggestionRejected) {
    return hint('suggestion_rejected')
  }
  if (brandChanged) {
    return hint('wrong_brand')
  }
  return packageChanged ? hint('wrong_packaging') : hint('wrong_price')
}

export const ImportRowEditPanel = ({
  row,
  selectedProductTypeId,
  typePickedByOperator = false,
  fieldOverride,
  productTypes,
  brandOptions = [],
  countryOptions = [],
  onTypeChange,
  onFieldOverrideChange,
}: ImportRowEditPanelProps) => {
  const [name, setName] = useState(fieldOverride?.name ?? row.name ?? '')
  const [brand, setBrand] = useState(fieldOverride?.brand ?? row.brand ?? '')
  const [country, setCountry] = useState(fieldOverride?.country ?? row.country ?? '')
  const [price, setPrice] = useState(fieldOverride?.price ?? row.price ?? '')
  const [unit, setUnit] = useState(fieldOverride?.unit ?? row.unit ?? '')
  const [minShipmentQty, setMinShipmentQty] = useState(
    fieldOverride?.minShipmentQty ?? row.minShipmentQty ?? '',
  )
  const [packageSize, setPackageSize] = useState(fieldOverride?.packageSize ?? row.packageSize ?? '')
  const [comment, setComment] = useState(fieldOverride?.comment ?? '')

  const correctionReason = inferCorrectionReason({
    previousProductTypeId: row.previousProductTypeId,
    chosenProductTypeId: selectedProductTypeId,
    matcherSuggestedTypeId: row.matchPreview.candidates[0]?.productTypeId ?? null,
    operatorPickedType: typePickedByOperator,
    previousBrand: row.brand,
    chosenBrand: brand,
    previousPackageSize: row.packageSize,
    chosenPackageSize: packageSize,
    previousPrice: row.price,
    chosenPrice: price,
  })

  useEffect(() => {
    setName(fieldOverride?.name ?? row.name ?? '')
    setBrand(fieldOverride?.brand ?? row.brand ?? '')
    setCountry(fieldOverride?.country ?? row.country ?? '')
    setPrice(fieldOverride?.price ?? row.price ?? '')
    setUnit(fieldOverride?.unit ?? row.unit ?? '')
    setMinShipmentQty(fieldOverride?.minShipmentQty ?? row.minShipmentQty ?? '')
    setPackageSize(fieldOverride?.packageSize ?? row.packageSize ?? '')
    setComment(fieldOverride?.comment ?? '')
  }, [
    row.rowNumber,
    fieldOverride,
    row.name,
    row.brand,
    row.country,
    row.price,
    row.unit,
    row.minShipmentQty,
    row.packageSize,
  ])

  const notifyFieldOverride = (
    nextName: string,
    nextBrand: string,
    nextCountry: string,
    nextPrice: string,
    nextUnit: string,
    nextMinShipmentQty: string,
    nextPackageSize: string,
    nextComment: string,
  ) => {
    onFieldOverrideChange(
      buildFieldOverride(
        row,
        nextName,
        nextBrand,
        nextCountry,
        nextPrice,
        nextUnit,
        nextMinShipmentQty,
        nextPackageSize,
        nextComment,
      ),
    )
  }

  return (
    <div className="market-catalog-edit">
      <div className="market-catalog-edit__meta">
        {row.externalSku ? <span>Артикул: {row.externalSku}</span> : null}
        {row.unit ? <span>Ед.: {row.unit}</span> : null}
        {row.minShipmentQty ? <span>Мин. отгрузка: {row.minShipmentQty}</span> : null}
        {row.packageSize ? <span>Упаковка: {row.packageSize}</span> : null}
        {row.category ? <span>Категория из файла: {row.category}</span> : null}
      </div>

      <div className="market-catalog-edit__grid">
        <label className="echat-field market-catalog-edit__field--wide">
          <span className="echat-field__label">Наименование</span>
          <input
            className="echat-field__input"
            value={name}
            required
            onChange={(event) => {
              const value = event.target.value
              setName(value)
              notifyFieldOverride(value, brand, country, price, unit, minShipmentQty, packageSize, comment)
            }}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Цена</span>
          <input
            className="echat-field__input"
            inputMode="decimal"
            value={price}
            onChange={(event) => {
              const value = event.target.value
              setPrice(value)
              notifyFieldOverride(name, brand, country, value, unit, minShipmentQty, packageSize, comment)
            }}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Ед. изм.</span>
          <input
            className="echat-field__input"
            value={unit}
            onChange={(event) => {
              const value = event.target.value
              setUnit(value)
              notifyFieldOverride(name, brand, country, price, value, minShipmentQty, packageSize, comment)
            }}
          />
        </label>
        <SuggestTextField
          label="Бренд"
          value={brand}
          suggestions={brandOptions}
          listId={`import-row-${row.rowNumber}-brand`}
          onChange={(value) => {
            setBrand(value)
            notifyFieldOverride(name, value, country, price, unit, minShipmentQty, packageSize, comment)
          }}
        />
        <SuggestTextField
          label="Страна"
          value={country}
          suggestions={countryOptions}
          listId={`import-row-${row.rowNumber}-country`}
          onChange={(value) => {
            setCountry(value)
            notifyFieldOverride(name, brand, value, price, unit, minShipmentQty, packageSize, comment)
          }}
        />
        <label className="echat-field">
          <span className="echat-field__label">Мин. отгрузка</span>
          <input
            className="echat-field__input"
            inputMode="decimal"
            value={minShipmentQty}
            onChange={(event) => {
              const value = event.target.value
              setMinShipmentQty(value)
              notifyFieldOverride(name, brand, country, price, unit, value, packageSize, comment)
            }}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Упаковка</span>
          <input
            className="echat-field__input"
            value={packageSize}
            onChange={(event) => {
              const value = event.target.value
              setPackageSize(value)
              notifyFieldOverride(name, brand, country, price, unit, minShipmentQty, value, comment)
            }}
          />
        </label>
        <ProductTypeCascadePicker
          productTypes={productTypes}
          value={selectedProductTypeId}
          onChange={onTypeChange}
          className="market-product-type-cascade market-product-type-cascade--form"
          emptyLabel="— из матчинга —"
          idPrefix={`import-row-${row.rowNumber}`}
        />
      </div>

      {row.matchPreview.candidates.length > 0 ? (
        <div className="market-catalog-edit__suggestions">
          <p className="echat-field__label">Подсказки матчера</p>
          <div className="market-inline-actions">
            {row.matchPreview.candidates.slice(0, 5).map((candidate) => (
              <button
                key={candidate.productTypeId}
                type="button"
                className="echat-btn echat-btn--ghost echat-btn--sm"
                onClick={() => onTypeChange(candidate.productTypeId)}
              >
                {candidate.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {correctionReason ? (
        <div className="market-catalog-edit__suggestions">
          <p className="echat-field__label">Причина правки</p>
          <span className="badge">{correctionReason.label}</span>
          {correctionReason.value === 'other' ? (
            <p className="echat-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 4 }}>
              Изменилось несколько полей сразу — уточните, что не так, в комментарии ниже.
            </p>
          ) : null}
        </div>
      ) : null}

      <label className="echat-field">
        <span className="echat-field__label">Комментарий к строке</span>
        <textarea
          className="echat-field__input"
          rows={2}
          value={comment}
          onChange={(event) => {
            const value = event.target.value
            setComment(value)
            notifyFieldOverride(name, brand, country, price, unit, minShipmentQty, packageSize, value)
          }}
        />
      </label>
    </div>
  )
}

export const effectiveImportRowTypeId = (
  row: ImportPreviewRowDto,
  rowTypeOverrides: Record<number, number>,
): number | null => {
  const override = rowTypeOverrides[row.rowNumber]
  if (override != null) {
    return override
  }
  const suggested = row.matchPreview.candidates[0]?.productTypeId
  if (suggested != null) {
    return suggested
  }
  return row.matchPreview.decision.toLowerCase() === 'manual'
    ? row.previousProductTypeId
    : null
}

/**
 * Types to send on confirm: only explicit user edits.
 * Do not pin preview rematch results — on price-list reimport that would overwrite
 * existing categories (manual/auto/suggested) via backend override → manual path.
 * Unedited rows rematch or preserve on the server (CatalogMatchStatusPolicy).
 */
export const buildConfirmProductTypeOverrides = (
  _rows: ImportPreviewRowDto[],
  rowTypeOverrides: Record<number, number>,
  excludedRowNumbers: ReadonlySet<number> = new Set(),
): Record<number, number> => {
  const overrides: Record<number, number> = {}
  for (const [rowKey, typeId] of Object.entries(rowTypeOverrides)) {
    const rowNumber = Number(rowKey)
    if (!Number.isFinite(rowNumber) || excludedRowNumbers.has(rowNumber)) {
      continue
    }
    overrides[rowNumber] = typeId
  }
  return overrides
}

/**
 * Верхние кандидаты матчера по тем строкам, где оператор явно выбрал тип. Уходят на
 * confirm рядом с оверрайдами: бэк при явном выборе матчер уже не гоняет и без этого не
 * отличит «оставил свой тип, потому что матчер согласен» от «оставил вопреки другому
 * предложению». Второе — прямой сигнал об ошибке матчера, и он записывается как
 * suggestion_rejected.
 */
export const buildConfirmMatcherSuggestions = (
  rows: ImportPreviewRowDto[],
  rowTypeOverrides: Record<number, number>,
  excludedRowNumbers: ReadonlySet<number> = new Set(),
): Record<number, number> => {
  const suggestions: Record<number, number> = {}
  for (const row of rows) {
    if (excludedRowNumbers.has(row.rowNumber) || rowTypeOverrides[row.rowNumber] == null) {
      continue
    }
    const suggested = row.matchPreview.candidates[0]?.productTypeId
    if (suggested != null) {
      suggestions[row.rowNumber] = suggested
    }
  }
  return suggestions
}

export const importRowTypeLabel = (
  row: ImportPreviewRowDto,
  rowTypeOverrides: Record<number, number>,
  typeNames: Map<number, string>,
): string => {
  const typeId = effectiveImportRowTypeId(row, rowTypeOverrides)
  if (typeId == null) {
    return '—'
  }
  const best = row.matchPreview.candidates.find((candidate) => candidate.productTypeId === typeId)
  const name = typeNames.get(typeId) ?? best?.name
  return name ?? `Тип #${typeId}`
}
